// src/components/Footer.jsx
import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer id="footer" className="bg-[#070D14] text-white py-14 px-6 border-t border-slate-800 dark:border-white/10 relative overflow-hidden">
      {/* Decorative Glow */}
      <div className="pointer-events-none absolute left-1/2 bottom-0 -translate-x-1/2 h-40 w-full max-w-4xl rounded-full bg-blue-500/10 blur-3xl" />

      <div className="max-w-7xl mx-auto space-y-12 relative z-10">
        {/* Main Columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand Info */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-3">
              <img
                src="/assets/images/henki_logo.svg"
                alt="Henki Logo"
                className="w-10 h-10"
              />
              <span className="text-3xl font-extrabold text-white tracking-tight">
                Henki
              </span>
            </div>

            <p className="text-sm text-gray-300 leading-relaxed max-w-sm">
              {t("footer.tagline")}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white mb-4">
              {t("footer.customerService")}
            </h3>
            <ul className="space-y-2.5 text-sm text-gray-300">
              <li>
                <Link to="/privacidad" className="hover:text-cyan-300 transition">
                  {t("footer.privacy")}
                </Link>
              </li>
              <li>
                <Link to="/terminos" className="hover:text-cyan-300 transition">
                  {t("footer.terms")}
                </Link>
              </li>
              <li>
                <a href="/#servicios" className="hover:text-cyan-300 transition">
                  {t("footer.servicesLink")}
                </a>
              </li>
              <li>
                <Link to="/planes" className="hover:text-cyan-300 transition">
                  {t("nav.plans") || "Planes Mensuales"}
                </Link>
              </li>
              <li>
                <Link to="/nosotros" className="hover:text-cyan-300 transition">
                  {t("footer.aboutLink")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Social Networks */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white mb-4">
              {t("footer.followUs")}
            </h3>
            <ul className="space-y-2.5 text-sm text-gray-300">
              <li>
                <a
                  href="https://www.youtube.com/@henkivzla"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 hover:text-red-400 transition"
                >
                  <img src="/assets/icons/youtube.svg" alt="YouTube" className="w-4 h-4" />
                  <span>YouTube</span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/henkivzla/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 hover:text-pink-400 transition"
                >
                  <img src="/assets/icons/instagram.svg" alt="Instagram" className="w-4 h-4" />
                  <span>Instagram</span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.tiktok.com/@henkivzla"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 hover:text-cyan-400 transition"
                >
                  <img src="/assets/icons/tiktok.svg" alt="TikTok" className="w-4 h-4" />
                  <span>TikTok</span>
                </a>
              </li>
              <li>
                <a
                  href="https://ve.linkedin.com/company/henki-com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 hover:text-blue-400 transition"
                >
                  <img src="/assets/icons/linkedin_icon.svg" alt="LinkedIn" className="w-4 h-4" />
                  <span>LinkedIn</span>
                </a>
              </li>
              <li>
                <a
                  href="https://play.google.com/store/apps/developer?id=henkivzla"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 hover:text-emerald-400 transition"
                >
                  <img src="/assets/icons/google_play_icon.svg" alt="Google Play" className="w-4 h-4" />
                  <span>Google Play Developer</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white mb-4">
              {t("footer.contact")}
            </h3>
            <ul className="space-y-2.5 text-sm text-gray-300">
              <li>
                <a
                  href="mailto:contacto@henki.com.ve"
                  className="hover:text-white transition break-all"
                >
                  ✉️ contacto@henki.com.ve
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/584228180393"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-emerald-400 transition"
                >
                  📱 +58 422 8180393
                </a>
              </li>
              <li className="pt-2 text-xs text-gray-400">
                {t("footer.location")}
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Rights */}
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-400">
          <p>{t("footer.copyright")}</p>
          <p className="font-semibold text-cyan-300">
            {t("footer.developedBy")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;